// Sidebar navigation preferences store — the same persistence pattern as the
// dashboard layout store: a vanilla zustand store over localStorage with a
// versioned key. The key carries the user id when the auth scope knows it
// (preferences follow the account across devices within this browser); a
// signed-out session degrades to a shared per-device key.

import { useStore } from 'zustand';
import { createStore, type StoreApi } from 'zustand/vanilla';

import { NAV_GROUPS } from '../../components/layout/nav-items.js';
import { useScopeStore } from '../../shared/scope/useCurrentScope.js';
import {
  DEFAULT_NAVIGATION_PREFERENCES,
  applyNavigationPreferences,
  parseNavigationPreferences,
  type NavigationPreferences,
} from './navigationPreferences.js';

/** Bump on breaking preference-shape changes to retire stored layouts. */
const NAV_PREFERENCES_STORAGE_VERSION = 1;
const ANONYMOUS_USER_KEY = 'anon';

function storageKeyFor(userId: string | null): string {
  const owner = userId ?? ANONYMOUS_USER_KEY;
  return `govardos-sidebar-nav-v${NAV_PREFERENCES_STORAGE_VERSION}-${owner}`;
}

const memoryStorage: Record<string, string> = {};

function getStorage(): {
  getItem: (k: string) => string | null;
  setItem: (k: string, v: string) => void;
  removeItem: (k: string) => void;
} {
  try {
    if (
      typeof window !== 'undefined' &&
      window.localStorage &&
      typeof window.localStorage.getItem === 'function'
    ) {
      return window.localStorage;
    }
  } catch {
    // fall through to the memory fallback
  }
  return {
    getItem: (k) => memoryStorage[k] ?? null,
    setItem: (k, v) => {
      memoryStorage[k] = v;
    },
    removeItem: (k) => {
      delete memoryStorage[k];
    },
  };
}

function currentUserId(): string | null {
  return useScopeStore.getState().user?.id ?? null;
}

function readPersistedPreferences(userId: string | null): NavigationPreferences | null {
  try {
    const raw = getStorage().getItem(storageKeyFor(userId));
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    return parseNavigationPreferences(parsed);
  } catch {
    return null;
  }
}

function persistPreferences(userId: string | null, preferences: NavigationPreferences): void {
  try {
    getStorage().setItem(storageKeyFor(userId), JSON.stringify(preferences));
  } catch {
    // localStorage may be unavailable; the store still works in memory.
  }
}

export interface NavigationPreferencesState {
  preferences: NavigationPreferences;
  /** Groups after applying preferences (before role/flag filtering). */
  orderedGroups: ReturnType<typeof applyNavigationPreferences>;
  moveGroup: (groupKey: string, direction: -1 | 1) => void;
  moveItem: (groupKey: string, itemRoute: string, direction: -1 | 1) => void;
  toggleItemHidden: (itemRoute: string) => void;
  resetPreferences: () => void;
}

function groupsWithPreferences(preferences: NavigationPreferences) {
  return applyNavigationPreferences(NAV_GROUPS, preferences);
}

function effectiveGroupOrder(preferences: NavigationPreferences): readonly string[] {
  const order = [...preferences.groupOrder];
  for (const group of NAV_GROUPS) {
    if (!order.includes(group.key)) order.push(group.key);
  }
  return order;
}

function effectiveItemOrder(
  preferences: NavigationPreferences,
  groupKey: string,
): readonly string[] {
  const preferred = preferences.itemOrder[groupKey];
  const defaultRoutes =
    NAV_GROUPS.find((group) => group.key === groupKey)?.items.map((item) => item.to) ?? [];
  if (preferred === undefined) return defaultRoutes;
  const ordered = [...preferred];
  for (const route of defaultRoutes) {
    if (!ordered.includes(route)) ordered.push(route);
  }
  return ordered;
}

function moveEntry<T>(entries: readonly T[], value: T, direction: -1 | 1): readonly T[] {
  const index = entries.indexOf(value);
  const targetIndex = index + direction;
  if (index === -1 || targetIndex < 0 || targetIndex >= entries.length) {
    return entries;
  }
  const next = [...entries];
  const current = next[index]!;
  next[index] = next[targetIndex]!;
  next[targetIndex] = current;
  return next;
}

function createNavigationPreferencesStore(): StoreApi<NavigationPreferencesState> {
  const initialUserId = currentUserId();
  const persisted = readPersistedPreferences(initialUserId);
  const preferences = persisted ?? DEFAULT_NAVIGATION_PREFERENCES;

  const store = createStore<NavigationPreferencesState>((set, get) => ({
    preferences,
    orderedGroups: groupsWithPreferences(preferences),
    moveGroup: (groupKey, direction) => {
      const { preferences: current } = get();
      const next: NavigationPreferences = {
        ...current,
        groupOrder: moveEntry(effectiveGroupOrder(current), groupKey, direction),
      };
      set({ preferences: next, orderedGroups: groupsWithPreferences(next) });
      persistPreferences(currentUserId(), next);
    },
    moveItem: (groupKey, itemRoute, direction) => {
      const { preferences: current } = get();
      const next: NavigationPreferences = {
        ...current,
        itemOrder: {
          ...current.itemOrder,
          [groupKey]: moveEntry(effectiveItemOrder(current, groupKey), itemRoute, direction),
        },
      };
      set({ preferences: next, orderedGroups: groupsWithPreferences(next) });
      persistPreferences(currentUserId(), next);
    },
    toggleItemHidden: (itemRoute) => {
      const { preferences: current } = get();
      const hidden = new Set(current.hiddenItems);
      if (hidden.has(itemRoute)) {
        hidden.delete(itemRoute);
      } else {
        hidden.add(itemRoute);
      }
      const next: NavigationPreferences = { ...current, hiddenItems: [...hidden] };
      set({ preferences: next, orderedGroups: groupsWithPreferences(next) });
      persistPreferences(currentUserId(), next);
    },
    resetPreferences: () => {
      set({
        preferences: DEFAULT_NAVIGATION_PREFERENCES,
        orderedGroups: groupsWithPreferences(DEFAULT_NAVIGATION_PREFERENCES),
      });
      try {
        getStorage().removeItem(storageKeyFor(currentUserId()));
      } catch {
        // removal is best-effort
      }
    },
  }));

  // Account switch (login/logout): re-read the other user's preferences.
  let trackedUserId = initialUserId;
  useScopeStore.subscribe((state) => {
    const userId = state.user?.id ?? null;
    if (userId === trackedUserId) return;
    trackedUserId = userId;
    const next = readPersistedPreferences(userId) ?? DEFAULT_NAVIGATION_PREFERENCES;
    store.setState({
      preferences: next,
      orderedGroups: groupsWithPreferences(next),
    });
  });

  return store;
}

const navigationPreferencesStore = createNavigationPreferencesStore();

export function useNavigationPreferencesStore<T>(
  selector: (state: NavigationPreferencesState) => T,
): T {
  return useStore(navigationPreferencesStore, selector);
}

export function resetNavigationPreferencesStoreForTest(): void {
  navigationPreferencesStore.setState({
    preferences: DEFAULT_NAVIGATION_PREFERENCES,
    orderedGroups: groupsWithPreferences(DEFAULT_NAVIGATION_PREFERENCES),
  });
}
